import { useCallback, useContext, useState } from "react";

import { css } from "@emotion/react";

import { DEVICE_ACTIONS, DialogProps } from "components/Actions/types";
import { ALERT_STATUS } from "context/alert/types/enums";
import ParamsContext from "context/params/paramsContext";
import {
    CONFIGURATION_FEATURE_SETTINGS_FILE_TYPES,
    ENDPOINT_PATH_SEGMENTS,
    ENDPOINTS,
    TABLE_NAMES,
    TRI_SOURCES
} from "shared/types/enums";

import DialogActions from "../../components/DialogActions";
import TriSource from "../../components/TriSource/TriSource";
import { useTriSourceAction } from "../hooks";
import CompatibilityContent from "./CompatibilityContent";
import { BACK, CLOSE, NEXT, SAVE } from "./constants/buttons";
import { FILE_TYPE_OPTIONS } from "./constants/fileTypeOptions";
import { getFeatureEntries } from "./functions";
import SingleFeature from "./SingleFeature";
import { CompatibilityData, Features } from "./types/interfaces";
import { FeatureValue } from "./types/types";

const AutoSync = ({ isOpen, close }: DialogProps) => {
    const { setReloadItems } = useContext(ParamsContext);

    const {
        t,
        rootCompany,
        isSuperUser,
        activeSource,
        sourcePayload,
        isSourceReady,
        isSelectedSingleItem,
        setAlert,
        setBackgroundActionId,
        deselectAllRows,
        postData,
        updateData,
        bulkUpdateData,
        handleResponse
    } = useTriSourceAction();

    const [isFinalStep, setIsFinalStep] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const [compatibilityData, setCompatibilityData] =
        useState<CompatibilityData | null>(null);

    const [features, setFeatures] = useState<Features>({
        [CONFIGURATION_FEATURE_SETTINGS_FILE_TYPES.MainConfigurationSync]: null,
        [CONFIGURATION_FEATURE_SETTINGS_FILE_TYPES.OneWireAuthListSync]: null,
        [CONFIGURATION_FEATURE_SETTINGS_FILE_TYPES.BluetoothAuthListSync]: null,
        [CONFIGURATION_FEATURE_SETTINGS_FILE_TYPES.ManualGeofenceListSync]: null
    });

    const isAtLeastOneSelected = Object.values(features).some(
        value => value !== null
    );

    const areAllDevicesIncompatible =
        compatibilityData?.compatible_devices_count === 0;

    const isReady =
        isSourceReady &&
        sourcePayload !== null &&
        isAtLeastOneSelected &&
        !areAllDevicesIncompatible;

    const handleCloseDialog = () => close(DEVICE_ACTIONS.AutoSync);

    const onSecondaryBtnClick = () => {
        if (isFinalStep) {
            changeFinalStep(false);
            setCompatibilityData(null);
        } else {
            handleCloseDialog();
        }
    };

    const changeFinalStep = (isFinalStepValue: boolean) =>
        setIsFinalStep(isFinalStepValue);

    const onFeatureChange = useCallback(
        (
            fileType: CONFIGURATION_FEATURE_SETTINGS_FILE_TYPES,
            value: FeatureValue
        ) =>
            setFeatures(prev => ({
                ...prev,
                [fileType]: value
            })),
        []
    );

    const getFormData = () => {
        const formData = new FormData();

        formData.append("source", activeSource);
        formData.append("file", sourcePayload as Blob);
        isSuperUser && formData.append("root_id", String(rootCompany?.id));

        const featureEntries = getFeatureEntries(features);

        featureEntries.forEach(([fileType, value]) => {
            if (value === null) return;

            formData.append(`data[feature_settings][${fileType}]`, value);
        });

        return formData;
    };

    const getPayloadData = () => {
        const data = {
            feature_settings: features
        };

        const payload = {
            source: activeSource,
            [activeSource]: sourcePayload,
            data
        };

        if (isFinalStep) {
            return isSelectedSingleItem ? data : payload;
        }

        return payload;
    };

    const getPayload = () =>
        activeSource === TRI_SOURCES.FromFile
            ? getFormData()
            : getPayloadData();

    const deselectAndClose = () => {
        deselectAllRows();
        handleCloseDialog();
    };

    const submitAutoSync = async () => {
        try {
            setIsLoading(true);

            const payloadData = getPayload();

            const { data } = await (isSelectedSingleItem
                ? updateData(
                      ENDPOINTS.Devices,
                      (sourcePayload as number[])[0],
                      payloadData
                  )
                : bulkUpdateData(ENDPOINTS.Devices, payloadData));

            if (isSelectedSingleItem) {
                deselectAndClose();

                setAlert({
                    status: ALERT_STATUS.Success,
                    title: t("Flash##Device was updated.")
                });
            } else {
                setBackgroundActionId(
                    data,
                    TABLE_NAMES.Devices,
                    () => {
                        deselectAndClose();
                        setReloadItems(TABLE_NAMES.Devices);
                    },
                    () => setIsLoading(false)
                );
            }
        } catch (error) {
            handleResponse(error);
            setIsLoading(false);
        }
    };

    const checkCompatibility = async () => {
        try {
            setIsLoading(true);

            const payloadData = getPayload();

            const { data } = await postData(
                `${ENDPOINTS.Devices}${ENDPOINT_PATH_SEGMENTS.BulkUpdate}${ENDPOINT_PATH_SEGMENTS.CompatibilityCheck}`,
                payloadData
            );

            setCompatibilityData(data);
            changeFinalStep(true);
        } catch (error) {
            handleResponse(error);
        }

        setIsLoading(false);
    };

    const handleSubmit = () => {
        if (isFinalStep) {
            submitAutoSync();
        } else {
            checkCompatibility();
        }
    };

    const getSubmitBtnTooltipText = () => {
        if (areAllDevicesIncompatible) {
            return t("Dialog##auto sync toggle##tooltips##disabled submit");
        }

        return "";
    };

    const isBulkActionLoading = !isSelectedSingleItem && isLoading;
    const confirmationButtonText = t(`Button##${isFinalStep ? SAVE : NEXT}`);
    const secondaryButtonText = t(`Button##${isFinalStep ? BACK : CLOSE}`);

    return (
        <TriSource
            title={t("Dialog##auto sync")}
            description={t("Dialog##auto sync toggle##descriptions##dialog")}
            confirmationDescription={t(
                "Dialog##confirmations##auto sync confirmation"
            )}
            close={handleCloseDialog}
            isBulkActionLoading={isBulkActionLoading}
            isLoading={isLoading}
            open={isOpen}
            submit={handleSubmit}
            hideTriSource={isFinalStep}
            hasIgnoreConfirmation={isFinalStep}
            actions={
                <DialogActions
                    onClose={onSecondaryBtnClick}
                    isLoading={isBulkActionLoading || isLoading}
                    loaderText={
                        !isSelectedSingleItem ? t("Button##saving") : undefined
                    }
                    isReadyToConfirm={isReady}
                    submitBtnTooltipText={getSubmitBtnTooltipText()}
                    confirmationButtonText={confirmationButtonText}
                    secondaryButtonText={secondaryButtonText}
                />
            }
            slotProps={{
                transition: {
                    onExited: () => {
                        setIsFinalStep(false);
                        setIsLoading(false);
                        setCompatibilityData(null);

                        setFeatures({
                            [CONFIGURATION_FEATURE_SETTINGS_FILE_TYPES.MainConfigurationSync]:
                                null,
                            [CONFIGURATION_FEATURE_SETTINGS_FILE_TYPES.OneWireAuthListSync]:
                                null,
                            [CONFIGURATION_FEATURE_SETTINGS_FILE_TYPES.BluetoothAuthListSync]:
                                null,
                            [CONFIGURATION_FEATURE_SETTINGS_FILE_TYPES.ManualGeofenceListSync]:
                                null
                        });
                    }
                }
            }}
        >
            {isFinalStep && compatibilityData ? (
                <CompatibilityContent
                    compatibilityData={compatibilityData}
                    features={features}
                />
            ) : (
                <div
                    css={css({
                        marginBottom: "16px",

                        "& > div + div": {
                            marginTop: "24px"
                        }
                    })}
                >
                    {FILE_TYPE_OPTIONS.map(fileType => (
                        <SingleFeature
                            key={fileType}
                            fileType={fileType}
                            value={features[fileType]}
                            onChange={onFeatureChange}
                        />
                    ))}
                </div>
            )}
        </TriSource>
    );
};

export default AutoSync;
